import { useCallback } from 'react';
import {
  AiAdminOwner,
  AiChatConfig,
  AiConversationDetail,
  AiConversationSummary,
  AiEmailDecisionRecord,
} from '../../../types';
import { REACT_APP_API_BASE_URL } from '../../env';
import { useProtectedRoute } from './useProtectedRoute';

// JSON endpoints for StackIQ. The chat SSE endpoint is NOT here — it needs a
// streaming reader, see src/hooks/useAiChat.ts.
export type SentEmail = {
  _id: string;
  template: string;
  to?: string;
  from?: string;
  replyTo?: string;
  bcc?: string;
  subject?: string;
  html?: string;
  text?: string;
  sentAt: string;
  delivered: boolean;
  error?: string;
};

export default function useAiApiRoutes() {
  const protectedRoute = useProtectedRoute();

  const getChatConfig = useCallback(
    async (): Promise<AiChatConfig | undefined> =>
      protectedRoute({ method: 'get', url: `${REACT_APP_API_BASE_URL}/ai/chat-config` }),
    [protectedRoute]
  );

  const getHistory = useCallback(
    async (): Promise<{ conversations: AiConversationSummary[] } | undefined> =>
      protectedRoute({ method: 'get', url: `${REACT_APP_API_BASE_URL}/ai/history` }),
    [protectedRoute]
  );

  const getConversation = useCallback(
    async (id: string): Promise<AiConversationDetail | undefined> =>
      protectedRoute({ method: 'get', url: `${REACT_APP_API_BASE_URL}/ai/history/${id}` }),
    [protectedRoute]
  );

  const getAdminConversations = useCallback(
    async (
      ownerUserId?: string,
      page = 1
    ): Promise<
      | {
          totalItems: number;
          currentPage: number;
          totalPages: number;
          conversations: AiConversationSummary[];
        }
      | undefined
    > =>
      protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/conversations`,
        params: { owner_user_id: ownerUserId || undefined, page },
      }),
    [protectedRoute]
  );

  const getAdminConversation = useCallback(
    async (id: string): Promise<AiConversationDetail | undefined> =>
      protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/conversations/${id}`,
      }),
    [protectedRoute]
  );

  const getAdminOwners = useCallback(
    async (): Promise<
      { owners: AiAdminOwner[]; enabledCount: number; totalCount: number } | undefined
    > =>
      protectedRoute({ method: 'get', url: `${REACT_APP_API_BASE_URL}/ai/admin/owners` }),
    [protectedRoute]
  );

  const toggleOwnerAi = useCallback(
    async (ownerId: string, enabled?: boolean): Promise<{ id: string; aiEnabled: boolean } | undefined> =>
      protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/owners/${ownerId}/toggle-ai`,
        data: typeof enabled === 'boolean' ? { enabled } : {},
      }),
    [protectedRoute]
  );

  const getEmailDecisions = useCallback(
    async (
      page = 1,
      status?: string,
      filters: { search?: string; shippingLineCode?: string; id?: string } = {}
    ): Promise<
      | { totalItems: number; currentPage: number; totalPages: number; records: AiEmailDecisionRecord[] }
      | undefined
    > =>
      protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/email-decisions`,
        params: {
          page,
          status: status || undefined,
          search: filters.search || undefined,
          shippingLineCode: filters.shippingLineCode || undefined,
          id: filters.id || undefined,
        },
      }),
    [protectedRoute]
  );

  const processEmail = useCallback(
    async (payload: { from?: string; subject?: string; text: string }): Promise<AiEmailDecisionRecord | undefined> =>
      protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/process-email`,
        data: payload,
      }),
    [protectedRoute]
  );

  // `decisions` carries admin corrections — [{ index, action, reason }].
  // Submitting them together produces one customer email for the whole
  // request rather than one per container.
  const applyEmailDecisions = useCallback(
    async (
      id: string,
      decisions: { index: number; action: 'accept' | 'decline'; reason?: string }[] = []
    ): Promise<AiEmailDecisionRecord | undefined> =>
      protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/email-decisions/${id}/apply`,
        data: { decisions },
      }),
    [protectedRoute]
  );

  // Clear a record from the review queue without applying anything — for
  // requests an admin already actioned by hand.
  const markEmailDecisionReviewed = useCallback(
    async (id: string): Promise<AiEmailDecisionRecord | undefined> =>
      protectedRoute({
        method: 'post',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/email-decisions/${id}/mark-reviewed`,
      }),
    [protectedRoute]
  );

  // What was actually sent to the shipping line and the customer for the
  // containers in this record.
  const getSentEmails = useCallback(
    async (id: string): Promise<{ emails: SentEmail[] }> => {
      const response = await protectedRoute({
        method: 'get',
        url: `${REACT_APP_API_BASE_URL}/ai/admin/email-decisions/${id}/sent-emails`,
      });
      return { emails: response?.emails ?? [] };
    },
    [protectedRoute]
  );

  return {
    getChatConfig,
    getHistory,
    getConversation,
    getAdminConversations,
    getAdminConversation,
    getAdminOwners,
    toggleOwnerAi,
    getEmailDecisions,
    processEmail,
    applyEmailDecisions,
    markEmailDecisionReviewed,
    getSentEmails,
  };
}
